import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Recycle, Leaf, Package, Trash2, TreePine, Droplet, Building, MoreHorizontal } from 'lucide-react';
import type { MaterialSummary } from '@/hooks/useImpactData';

interface MaterialSummaryCardProps {
  totalWeight: number;
  materials: MaterialSummary[];
}

const MATERIAL_ICONS: Record<string, React.ElementType> = {
  recycle: Recycle,
  leaf: Leaf,
  package: Package,
  trash: Trash2,
  tree: TreePine,
  droplet: Droplet,
  building: Building,
  'more-horizontal': MoreHorizontal,
};

const formatWeight = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
};

export const MaterialSummaryCard = ({ totalWeight, materials }: MaterialSummaryCardProps) => {
  // Filter materials with weight > 0 and take top 5
  const visibleMaterials = materials.filter(m => m.weight > 0).slice(0, 5);

  return (
    <Card className="bg-white border-border">
      <CardContent className="p-6">
        {/* Header row */}
        <div className="flex items-start justify-between pb-6 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-[#10261F]">Resumo de Resíduos Coletados</h2>
            <p className="text-sm text-gray-600">Total coletado no período selecionado</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-[#10261F]">
              {formatWeight(totalWeight)} kg
            </div>
            <div className="text-sm text-gray-600">Total Coletado</div>
          </div>
        </div>

        {/* Materials row without internal borders */}
        <div className="flex flex-wrap lg:flex-nowrap pt-6">
          {visibleMaterials.map((material) => {
            const IconComponent = MATERIAL_ICONS[material.icon] || Recycle;
            return (
              <div 
                key={material.material}
                className="flex-1 min-w-[140px] flex flex-col items-center text-center py-2 px-3"
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${material.color}15` }}
                >
                  <IconComponent 
                    className="h-7 w-7" 
                    style={{ color: material.color }}
                  />
                </div>
                <span className="text-xl font-bold text-[#10261F]">
                  {formatWeight(material.weight)} kg
                </span>
                <span className="text-sm text-gray-600">
                  de {material.material}
                </span>
                <span className="text-xs text-gray-500 mt-0.5">
                  {material.percentage.toFixed(1)}% do total
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
